import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Palette, Users, Sparkles, ArrowRight, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardOverview = () => {
  const { studio } = useOutletContext();
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({ wraps: 0, leads: 0, renders: 0 });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [wrapsRes, leadsRes, rendersRes] = await Promise.all([
          axios.get(`${API}/wraps`, { headers: getAuthHeader() }),
          axios.get(`${API}/leads`, { headers: getAuthHeader() }),
          axios.get(`${API}/renders/studio/me`, { headers: getAuthHeader() })
        ]);
        
        setStats({
          wraps: wrapsRes.data.length,
          leads: leadsRes.data.length,
          renders: rendersRes.data.length
        });
        setRecentLeads(leadsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    if (studio) {
      fetchStats();
    }
  }, [studio, getAuthHeader]);

  if (!studio) {
    return (
      <div className="text-center py-24">
        <h1 className="text-2xl font-bold mb-4">Welcome to WrapViz!</h1>
        <p className="text-white/60 mb-8">Let's set up your studio</p>
        <Link to="/app/onboarding">
          <Button className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]">
            Start Setup
          </Button>
        </Link>
      </div>
    );
  }

  const isActive = studio.subscription_status === 'active';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="dashboard-overview"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">{studio.name}</h1>
          <a 
            href={`/s/${studio.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/60 hover:text-white text-sm flex items-center gap-1"
          >
            wrapviz.com/s/{studio.slug}
            <ExternalLink size={14} />
          </a>
        </div>
        <div className="flex gap-2">
          {!isActive && (
            <Link to="/app/billing">
              <Button className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]">
                Activate Subscription
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Subscription Alert */}
      {!isActive && (
        <div className="bg-[#FFFFFF]/10 border border-[#FFFFFF]/30 rounded-sm p-4 mb-8 flex items-start gap-3">
          <AlertCircle className="text-[#FFFFFF] flex-shrink-0 mt-0.5" size={18} />
          <div>
            <p className="font-semibold text-[#FFFFFF]">Studio not active</p>
            <p className="text-white/60 text-sm">Subscribe to publish your studio and enable the visualizer for customers.</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Link to="/app/wraps" className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6 card-hover">
          <div className="flex items-center justify-between mb-4">
            <Palette className="text-[#FFFFFF]" size={24} />
            <ArrowRight className="text-white/20" size={18} />
          </div>
          <p className="text-3xl font-bold mb-1">{stats.wraps}</p>
          <p className="text-white/50 text-sm">Wraps</p>
        </Link>
        
        <Link to="/app/leads" className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6 card-hover">
          <div className="flex items-center justify-between mb-4">
            <Users className="text-[#FFFFFF]" size={24} />
            <ArrowRight className="text-white/20" size={18} />
          </div>
          <p className="text-3xl font-bold mb-1">{stats.leads}</p>
          <p className="text-white/50 text-sm">Leads</p>
        </Link>
        
        <Link to="/app/renders" className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6 card-hover">
          <div className="flex items-center justify-between mb-4">
            <Sparkles className="text-[#FFFFFF]" size={24} />
            <ArrowRight className="text-white/20" size={18} />
          </div>
          <p className="text-3xl font-bold mb-1">{stats.renders}</p>
          <p className="text-white/50 text-sm">Renders</p>
        </Link>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <h3 className="font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Link to="/app/wraps" className="block py-2 text-white/60 hover:text-white">
              → Add new wrap colors
            </Link>
            <Link to="/app/branding" className="block py-2 text-white/60 hover:text-white">
              → Update branding
            </Link>
            <Link to="/app/pages" className="block py-2 text-white/60 hover:text-white">
              → Edit homepage content
            </Link>
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Recent Leads</h3>
            <Link to="/app/leads" className="text-[#FFFFFF] text-sm hover:underline">View all</Link>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-10 bg-white/5 rounded animate-pulse"></div>
              ))}
            </div>
          ) : recentLeads.length === 0 ? (
            <p className="text-white/40 text-sm">No leads yet</p>
          ) : (
            <div className="space-y-2">
              {recentLeads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="font-medium">{lead.name}</p>
                    <p className="text-xs text-white/40">{lead.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    lead.status === 'new' ? 'bg-[#FFFFFF]/20 text-[#FFFFFF]' : 'bg-white/10 text-white/50'
                  }`}>
                    {lead.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardOverview;
